// Приметы клиента: по чему приложение узнаёт его снаружи — во встречах календаря, в выписках банка, в записи.
// Кто клиент, решает его постоянный номер (ID); примета лишь ведёт к нему и годится, только если она у одного клиента.
// Каждая примета приводится к виду для сравнения: телефон — цифрами с кодом страны, ник Telegram — строчными без «@»,
// почта — строчными, ссылка на видеовстречу — без «https://» и хвоста, написание имени — как normSig.
import { parseEventTitle } from './calendarTitle.ts';
import { normSig } from './statements.ts';

export type KeyKind = 'phone' | 'tg' | 'email' | 'link' | 'cal' | 'bank';
export const KEY_KINDS: readonly KeyKind[] = ['phone', 'tg', 'email', 'link', 'cal', 'bank'];
export const KEY_LABEL: Readonly<Record<KeyKind, string>> = {
  phone: 'Телефон · WhatsApp', tg: 'Telegram', email: 'Почта', link: 'Ссылка на видеовстречу',
  cal: 'Как пишется в календаре', bank: 'В выписке банка',
};

// Телефон: цифры с кодом страны. «8 916 …» и «916 …» (10 цифр с 9) — Россия, +7; «077 12 34 56» — Армения, +374.
export function normPhone(s: string | null | undefined): string {
  const raw = String(s ?? '').trim();
  let d = raw.replace(/\D/g, '');
  if (!d) return '';
  if (!raw.startsWith('+')) {
    if (d.startsWith('00')) d = d.slice(2);                              // 00374… — международный набор
    else if (d.length === 11 && d.startsWith('8')) d = '7' + d.slice(1);
    else if (d.length === 10 && d.startsWith('9')) d = '7' + d;
    else if (d.length === 9 && d.startsWith('0')) d = '374' + d.slice(1);
  }
  return d.length >= 10 && d.length <= 15 ? d : '';
}

// Ник Telegram: «@Anna_K», «t.me/Anna_K», «https://t.me/anna_k» → «anna_k» (в Telegram: 5–32 знака, с буквы).
export function normTg(s: string | null | undefined): string {
  const t = String(s ?? '').trim().toLowerCase();
  const m = /(?:^|\/\/|\s)(?:t|telegram)\.me\/([a-z][a-z0-9_]{4,31})/.exec(t);
  if (m) return m[1]!;
  const n = t.replace(/^@/, '');
  return /^[a-z][a-z0-9_]{4,31}$/.test(n) ? n : '';
}

export function normEmail(s: string | null | undefined): string {
  const t = String(s ?? '').trim().toLowerCase().replace(/^mailto:/, '');
  return /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/.test(t) ? t : '';
}

// Ссылка на видеовстречу — то, что её отличает: адрес без «https://», «www.», параметров и «/» в конце.
// Zoom — без поддомена (us02web.zoom.us → zoom.us). Просто сайт без пути — не примета.
export function normLink(s: string | null | undefined): string {
  const t = String(s ?? '').trim();
  if (!t || /\s/.test(t)) return '';
  let u: URL;
  try { u = new URL(/^https?:\/\//i.test(t) ? t : 'https://' + t); } catch { return ''; }
  let host = u.hostname.toLowerCase().replace(/^www\./, '');
  if (!host.includes('.')) return '';
  if (/(^|\.)zoom\.us$/.test(host)) host = 'zoom.us';
  const path = u.pathname.replace(/\/+$/, '').toLowerCase();
  return path ? host + path : '';
}

// Написание имени в календаре → ключ: без кода, номера, мессенджера, «отмены» и «?» (как в разборе названия).
export function calKey(s: string | null | undefined): string {
  return parseEventTitle(String(s ?? '').split('·')[0]).key;
}

// Ключи имени из названия встречи: сначала вместе с пометкой в скобках («Анна (рыжая)» → «анна рыжая»),
// потом без неё («анна»). Скобки после номера — примечание, их оба варианта одинаково не видят.
export function titleNameKeys(summary: string | null | undefined): string[] {
  const t = String(summary ?? '').split('·')[0]!;
  const full = parseEventTitle(t.replace(/[()]/g, ' ')).key, bare = parseEventTitle(t).key;
  return [...new Set([full, bare].filter(Boolean))];
}

// Привести примету к виду для сравнения; не похоже на свой вид — пусто.
export function normKey(kind: KeyKind, s: string | null | undefined): string {
  switch (kind) {
    case 'phone': return normPhone(s);
    case 'tg': return normTg(s);
    case 'email': return normEmail(s);
    case 'link': return normLink(s);
    case 'cal': return calKey(s);
    case 'bank': return normSig(s);
  }
}

// Общие слова — не имя клиента: запоминать их как написание нельзя, иначе все такие встречи уйдут одному клиенту.
const GENERIC = new Set(['консультация', 'консультации', 'сессия', 'сессии', 'встреча', 'встречи', 'клиент', 'клиентка',
  'пара', 'семья', 'группа', 'супервизия', 'интервизия', 'терапия', 'психотерапия', 'личная', 'личное', 'обед', 'перерыв',
  'занято', 'созвон', 'звонок', 'разбор', 'тренинг', 'вебинар', 'лекция', 'семинар', 'курс', 'учеба', 'урок', 'занятие',
  'прием', 'диагностика', 'первичная', 'первичный', 'пробная', 'онлайн', 'офлайн', 'новый', 'новая', 'резерв', 'бронь',
  'окно', 'запись', 'перенос', 'отмена', 'дела', 'работа', 'встреча с клиентом', 'новый клиент', 'новая клиентка',
  'личная терапия']);

export function isGenericName(key: string): boolean {
  const k = normSig(key).replace(/ё/g, 'е');
  if (k.length < 2 || GENERIC.has(k)) return true;
  return k.split(' ').every((w) => GENERIC.has(w) || /^\d+$/.test(w) || w.length < 2);
}

export interface FoundKey { kind: KeyKind; value: string }

// Приметы в свободном тексте (название, описание и место встречи, строка выписки): ссылки, ники Telegram, почты,
// телефоны. Номера сессий, даты и суммы телефоном не считаются: в телефоне не меньше 10 цифр.
export function keysInText(text: string | null | undefined): FoundKey[] {
  const t = String(text ?? ''), out: FoundKey[] = [], seen = new Set<string>();
  const add = (kind: KeyKind, value: string) => {
    if (value && !seen.has(kind + ':' + value)) { seen.add(kind + ':' + value); out.push({ kind, value }); }
  };
  let rest = t;
  for (const m of t.matchAll(/https?:\/\/[^\s<>"'`)\]]+/gi)) {
    const tg = normTg(m[0]);
    if (tg && /(?:t|telegram)\.me\//i.test(m[0])) add('tg', tg); else add('link', normLink(m[0]));
    rest = rest.replace(m[0], ' ');
  }
  for (const m of rest.matchAll(/[\w.+-]+@[\w-]+(?:\.[\w-]+)*\.[a-z]{2,}/gi)) {
    add('email', normEmail(m[0]));
    rest = rest.replace(m[0], ' ');
  }
  for (const m of rest.matchAll(/(?:^|[^\w@.])@([a-z][a-z0-9_]{4,31})(?![\w@])/gi)) add('tg', normTg(m[1]));
  for (const m of rest.matchAll(/(?:t|telegram)\.me\/([a-z][a-z0-9_]{4,31})/gi)) add('tg', normTg(m[1]));
  for (const m of rest.matchAll(/\+?\d[\d\s()‐-―-]{8,}\d/g)) add('phone', normPhone(m[0]));
  return out;
}

// ——— Узнавание клиента по приметам ———

export interface IdentityClient { id: string; name: string; archived: boolean }
export interface IdentityKey { clientId: string; kind: KeyKind; value: string }

export interface Identity {
  clients: Map<string, IdentityClient>;
  byKey: Map<string, Set<string>>;          // «вид:значение» → клиенты; имена клиентов — «cal:ключ имени»
  alias: Map<string, string>;               // старый номер клиента (объединили с другим) → нынешний
}

export function buildIdentity(clients: readonly IdentityClient[], keys: readonly IdentityKey[],
  alias: ReadonlyArray<{ oldId: string; clientId: string }> = []): Identity {
  const byKey = new Map<string, Set<string>>(), add = (k: string, id: string) => {
    if (!k.endsWith(':')) (byKey.get(k) ?? byKey.set(k, new Set()).get(k)!).add(id);
  };
  const map = new Map(clients.map((c) => [c.id, c]));
  // имя клиента как есть и как его понял бы разбор названия («Вероника тг» → «вероника»); пометка в скобках остаётся
  // частью имени: «Анна (рыжая)» — не просто «Анна»
  for (const c of clients) { add('cal:' + normSig(c.name), c.id); add('cal:' + calKey(c.name.replace(/[()]/g, ' ')), c.id); }
  for (const k of keys) if (map.has(k.clientId)) add(k.kind + ':' + k.value, k.clientId);
  return { clients: map, byKey, alias: new Map(alias.map((a) => [a.oldId, a.clientId])) };
}

// Кто это по номеру: объединённого клиента — к тому, с кем объединили.
export function resolveId(idx: Identity, id: string | null | undefined): string | null {
  let cur = id ?? '', hops = 0;
  while (cur && !idx.clients.has(cur) && idx.alias.has(cur) && hops++ < 10) cur = idx.alias.get(cur)!;
  return idx.clients.has(cur) ? cur : null;
}

export type IdentifyVia = 'tag' | 'email' | 'phone' | 'tg' | 'link' | 'name';

export interface IdentifyInput {
  tag?: string | null;                      // номер клиента, спрятанный во встрече
  emails?: readonly string[];               // гости встречи
  links?: readonly string[];                // ссылка Meet, точки входа
  text?: string;                            // название, описание, место — телефоны, ники, ссылки, почты
  nameKeys?: readonly string[];             // ключи имени из названия: с пометкой в скобках, потом без
}

export interface IdentifyResult {
  clientId: string | null;
  via: IdentifyVia | null;
  reason: '' | 'none' | 'many' | 'archived';
  candidates: string[];                     // кто подходит, когда однозначно не вышло
}

// Кто клиент: 1) номер, спрятанный во встрече; 2) почта гостя, телефон, ник, ссылка — если ведут к одному клиенту;
// 3) имя целиком — если оно у одного действующего клиента. Примета, которая есть у двоих (общий номер, тёзки), не
// решает. Приметы и имя указывают на разных — не угадываем. Подошёл только клиент из архива — спросить.
// Номер во встрече перебивает всё, кроме явного имени другого клиента в названии: встречу серии могли отдать другому.
export function identify(idx: Identity, p: IdentifyInput): IdentifyResult {
  const none = (reason: IdentifyResult['reason'], candidates: Iterable<string> = []): IdentifyResult =>
    ({ clientId: null, via: null, reason, candidates: [...new Set(candidates)] });
  const active = (id: string) => !idx.clients.get(id)?.archived;
  let byName: string[] = [];
  for (const key of p.nameKeys ?? []) {
    const ids = idx.byKey.get('cal:' + key);
    if (ids?.size) { byName = [...ids]; break; }
  }
  const nameActive = byName.filter(active), nameArchived = byName.filter((id) => !active(id));
  const tagged = resolveId(idx, p.tag);
  if (tagged) {
    if (!active(tagged)) return none('archived', [tagged]);
    if (nameActive.length && !nameActive.includes(tagged)) return none('many', [tagged, ...nameActive]);
    return { clientId: tagged, via: 'tag', reason: '', candidates: [] };
  }
  const strong = new Map<string, IdentifyVia>(), shared = new Set<string>();
  const hit = (kind: Exclude<KeyKind, 'cal' | 'bank'>, value: string) => {
    const ids = idx.byKey.get(kind + ':' + value);
    if (!ids?.size) return;
    if (ids.size > 1) { for (const id of ids) shared.add(id); return; }
    const id = [...ids][0]!;
    if (!strong.has(id)) strong.set(id, kind);
  };
  for (const e of p.emails ?? []) hit('email', normEmail(e));
  for (const l of p.links ?? []) hit('link', normLink(l));
  for (const k of keysInText(p.text)) if (k.kind !== 'cal' && k.kind !== 'bank') hit(k.kind, k.value);
  const strongActive = [...strong.keys()].filter(active);
  if (strongActive.length === 1) {
    const id = strongActive[0]!;
    if (nameActive.length && !nameActive.includes(id)) return none('many', [id, ...nameActive]);
    return { clientId: id, via: strong.get(id)!, reason: '', candidates: [] };
  }
  if (strongActive.length > 1) return none('many', strongActive);
  if (nameActive.length === 1) return { clientId: nameActive[0]!, via: 'name', reason: '', candidates: [] };
  if (nameActive.length > 1) return none('many', nameActive);
  const archived = [...nameArchived, ...[...strong.keys()].filter((id) => !active(id))];
  if (archived.length) return none('archived', archived);
  return none('none', [...shared].filter(active));
}

// Похожие клиенты для разбора: общее слово имени или начало имени. Самые похожие — первыми, не больше трёх.
export function similarClients(idx: Identity, nameKeys: readonly string[], limit = 3): string[] {
  const words = new Set(nameKeys.flatMap((k) => k.split(' ')).filter((w) => w.length >= 3));
  if (!words.size) return [];
  const scored: Array<[string, number]> = [];
  for (const c of idx.clients.values()) {
    if (c.archived) continue;
    const cw = normSig(c.name).split(' ').filter(Boolean);
    let s = 0;
    for (const w of cw) {
      if (words.has(w)) s += 2;
      else if (w.length >= 3 && [...words].some((x) => x.startsWith(w.slice(0, 3)))) s += 1;
    }
    if (s) scored.push([c.id, s]);
  }
  return scored.sort((a, b) => b[1] - a[1] || (idx.clients.get(a[0])!.name < idx.clients.get(b[0])!.name ? -1 : 1))
    .slice(0, limit).map(([id]) => id);
}
